import React from "react";
import { Phone, Calendar, Globe, Clock, ShieldCheck, MessageSquare, Activity, Headphones } from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, Lead, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  ParallelGrid, ConversationThread, FeatureCallout, AgentCard,
  FlowDiagram, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogAmyAiReceptionist24x7 = () => (
  <RichBlogLayout
    category="Front Office"
    eyebrow="The AI Receptionist"
    title="Amy: Every Call Answered, Every Workflow Completed — 24/7"
    subtitle="Your front desk can hold one phone at a time. Amy can hold every phone, in every language, around the clock — and she finishes the work, not just the conversation."
    author="Asha Team"
    date="May 2, 2026"
    readTime="9 min read"
  >
    <Prose>
      <p>
        Walk into a busy practice at 9:14 AM and look at the front desk. Two
        lines are blinking. A patient is checking in. The hygienist needs an
        eligibility check before the next prophy. And the call that came in at
        7:52 PM last night — the one about a swollen jaw — is still on a
        sticky note next to the monitor.
      </p>
      <p>
        Your front desk is heroic. They are also, structurally, a single
        thread. One human, one call, one moment at a time. Everything else is
        either waiting in queue or already lost.
      </p>
    </Prose>

    <AgentCard
      name="Amy"
      role="AI Receptionist · Voice + Booking + Triage"
      initial="A"
      description="Amy answers every inbound call instantly, in multiple languages, trained on thousands of real dental conversations. She doesn't just take messages — she completes appointments, reschedules, insurance questions, and after-hours triage end-to-end inside your PMS."
      channels={["Inbound voice", "Outbound voice", "PMS booking", "HIPAA compliant"]}
    />

    <Prose>
      <h2>The math of a single thread</h2>
      <p>
        A traditional receptionist can answer one call at a time. After hours,
        that number becomes zero. Multiply that by a typical practice that
        receives 60–120 inbound calls a day with 25–40% spilling into
        voicemail, and the picture is clear: a meaningful share of every
        practice's daily new-patient pipeline never reaches a human.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 0, suffix: "s", label: "Average hold time" },
        { value: 24, suffix: "/7", label: "Always available" },
        { value: 100, suffix: "%", label: "Calls answered" },
        { value: 12, prefix: "+", suffix: " langs", label: "Multilingual support" },
      ]}
    />

    <Prose>
      <p>
        Amy doesn't add another seat to the front desk. She removes the seat as
        the constraint. Every call is answered the moment it rings — and most
        importantly, every call ends with the work actually finished.
      </p>

      <h2>Amy completes the call — she doesn't just take it</h2>
      <p>
        The category most teams imagine when they hear "AI receptionist" is a
        smarter answering service: take a name, take a number, hand it back to
        a human. That's not what Amy is. Amy completes the call.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Voicemail or answering service"
      afterTitle="End-to-end completion"
      beforeItems={[
        "Takes a name and a phone number",
        "Hands it back to your team in the morning",
        "Patient now waits, often calls a competitor",
        "No documentation, no chart context, no triage",
      ]}
      afterItems={[
        "Books the appointment directly into your PMS — correct provider, correct operatory",
        "Handles reschedules, insurance questions, and family bookings",
        "Triages emergencies and loops in the on-call provider when needed",
        "Logs a HIPAA-compliant transcript and recording on the patient chart",
      ]}
    />

    <Prose>
      <h2>Inside a single Amy-handled call</h2>
      <p>
        Amy is trained on thousands of real dental conversations and is wired
        directly into the chart, the schedule, and your provider rules. That
        means she is not "an LLM with a phone number." She is a receptionist
        with a complete operating model.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Picks up instantly, in the patient's language",
          body:
            "Amy answers on the first ring, identifies the caller's intent in real time, and switches language automatically — Spanish, Mandarin, Vietnamese, Arabic, and more.",
        },
        {
          title: "Pulls the chart and the schedule in parallel",
          body:
            "Before the patient finishes their first sentence, Amy has the chart, the operatory grid, the provider's preferences, and outstanding balances on hand.",
        },
        {
          title: "Completes the workflow in the PMS",
          body:
            "Books, reschedules, cancels, transfers, or coordinates a multi-patient family appointment — directly in the same system your team uses.",
        },
        {
          title: "Triages clinical urgency",
          body:
            "If the call is a clinical emergency, Amy assesses severity, asks the right questions, and loops in the on-call provider or escalates to a human teammate.",
        },
        {
          title: "Documents everything on the chart",
          body:
            "Every call lands on the patient's timeline with a clean summary, a speaker-diarized transcript, sentiment, and the linked follow-up tasks already created.",
        },
      ]}
    />

    <PullQuote>
      Your front desk can only handle one call at a time. Anything that comes in
      after hours, most of the time that opportunity is just lost. Amy changes
      that completely.
    </PullQuote>

    <Prose>
      <h2>The 8:47 PM call</h2>
      <p>
        It's a Tuesday night. Your office closed at five. At 8:47 PM the phone
        rings — a patient with sudden swelling and pain. In the old model,
        that's a voicemail at best. The patient may call back tomorrow. They
        may not. They may end up in an ER.
      </p>
      <p>
        Here's what that same call looks like with Amy.
      </p>
    </Prose>

    <ConversationThread
      title="After-hours triage call · 8:47 PM"
      channel="Inbound voice"
      messages={[
        { from: "system", text: "Inbound call answered on first ring" },
        { from: "patient", text: "Hi, my name is Maria. I'm a patient. The right side of my jaw is really swollen and it's hurting a lot." },
        { from: "agent", text: "Hi Maria, I'm so sorry — let's get you taken care of right away. Did the swelling come on suddenly, and is there any fever?" },
        { from: "patient", text: "It started this evening. Maybe a low fever, I'm not sure." },
        { from: "agent", text: "Got it. Based on what you're describing this could need same-day attention. I'm reaching the on-call doctor right now and will get back to you within a minute. I'm also booking you a 9:00 AM emergency exam tomorrow as a backup." },
        { from: "system", text: "Looped in Dr. Patel · Booked emergency slot for 9:00 AM · Logged on Maria's chart" },
      ]}
    />

    <Prose>
      <h2>Operationally, this changes the morning</h2>
      <p>
        When your team walks in tomorrow, the emergency is already booked. The
        eligibility check is already running. The recording, the transcript,
        and the action items are already on the chart. There is no "sticky
        note next to the monitor."
      </p>
      <p>
        That's the part most practices miss when they evaluate AI voice. The
        conversation is the easy part. The systems work that follows the
        conversation — that's where the real time is recovered.
      </p>
    </Prose>

    <ParallelGrid
      title="Amy is doing all of this — at the same time"
      columns={3}
      items={[
        { title: "New patient booking", subtitle: "Family of 4, English", icon: Calendar, status: "active" },
        { title: "Reschedule", subtitle: "Crown seat → Friday 2pm", icon: Calendar, status: "active" },
        { title: "Insurance question", subtitle: "Spanish · benefits scan", icon: ShieldCheck, status: "active" },
        { title: "Emergency triage", subtitle: "Loops in on-call provider", icon: Headphones, status: "active" },
        { title: "Returning patient hygiene", subtitle: "Auto-matched to chart", icon: Phone, status: "done" },
        { title: "Voicemail return", subtitle: "From last night, 10:43 PM", icon: MessageSquare, status: "done" },
      ]}
    />

    <Prose>
      <h2>The compliance and trust layer</h2>
      <p>
        Every call Amy handles is recorded, transcribed, and stored in a
        HIPAA-compliant way. Your team gets full visibility — call summary,
        speaker diarization, sentiment, and any tasks Amy created — directly
        on the patient's chart. There are no separate dashboards to log into,
        no extra recording systems to maintain.
      </p>
    </Prose>

    <FlowDiagram
      title="What happens after every call ends"
      nodes={[
        { label: "Call completed", sublabel: "Amy hangs up", icon: Phone },
        { label: "Auto-transcribed", sublabel: "Speaker-diarized", icon: Activity },
        { label: "Tasks extracted", sublabel: "Routed to right teammate", icon: MessageSquare },
        { label: "Logged to chart", sublabel: "Searchable forever", icon: ShieldCheck },
      ]}
    />

    <FeatureCallout
      stat="Unlimited"
      label="Concurrent calls — no queue, ever"
      description="Amy scales horizontally. A surge at 8 AM doesn't create hold times — it creates parallel Amys."
      icon={Phone}
    />

    <Prose>
      <h2>The day-to-day shift</h2>
      <p>
        Amy doesn't replace the warm energy of your front desk — she reclaims
        it. The minutes your team currently spends greeting voicemails, fishing
        for charts, and re-asking patients to repeat insurance numbers go
        back into the room.
      </p>
      <p>
        From the patient side, the difference is even simpler. They never get
        sent to a recording. They never wait. They reach someone — instantly,
        in their language — who can actually help.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Most practices already accept that some calls will be missed. Amy makes
        that assumption obsolete. Every call answered, every workflow
        completed, every chart updated — quietly, in parallel, around the
        clock.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogAmyAiReceptionist24x7;
